import { useState, useEffect, useRef, useCallback } from 'react';
import OutlinedInput from '@mui/material/OutlinedInput';
import TextField from '@mui/material/TextField';

// Generic debounced wrapper that accepts any component
export const DebouncedWrapper = ({
  value: externalValue = '',
  onChange,
  delayTimeout = 500,
  element: Component,
  minLength,
  forceNotifyOnMount = false,
  ...props
}) => {
  const [internalValue, setInternalValue] = useState(externalValue);
  const timeoutRef = useRef(null);
  const isFirstRender = useRef(true);

  // Update internal value when external value changes
  useEffect(() => {
    setInternalValue(externalValue);
  }, [externalValue]);

  // Handle debounced changes
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Skip on mount unless forceNotifyOnMount is true
    if (isFirstRender.current) {
      isFirstRender.current = false;
      if (!forceNotifyOnMount) {
        return;
      }
    }

    // Check minimum length requirement
    if (minLength !== undefined && internalValue.length < minLength) {
      return;
    }

    timeoutRef.current = setTimeout(() => {
      if (onChange && internalValue !== externalValue) {
        onChange({ target: { value: internalValue } });
      }
    }, delayTimeout);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [internalValue, delayTimeout, onChange, externalValue, minLength, forceNotifyOnMount]);

  const handleChange = useCallback((e) => {
    setInternalValue(e.target.value);
  }, []);

  return <Component {...props} value={internalValue} onChange={handleChange} />;
};

// Base debounced input using OutlinedInput (lighter, more flexible)
export const DebouncedInput = ({
  delayTimeout = 500,
  onChange,
  value: externalValue = '',
  minLength,
  forceNotifyOnMount = false,
  ...inputProps
}) => {
  const [internalValue, setInternalValue] = useState(externalValue);
  const timeoutRef = useRef(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    setInternalValue(externalValue);
  }, [externalValue]);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (isFirstRender.current) {
      isFirstRender.current = false;
      if (!forceNotifyOnMount) {
        return;
      }
    }

    if (minLength !== undefined && internalValue.length < minLength) {
      return;
    }

    timeoutRef.current = setTimeout(() => {
      if (onChange && internalValue !== externalValue) {
        onChange({ target: { value: internalValue } });
      }
    }, delayTimeout);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [internalValue, delayTimeout, onChange, externalValue, minLength, forceNotifyOnMount]);

  const handleChange = useCallback((event) => {
    setInternalValue(event.target.value);
  }, []);

  return <OutlinedInput {...inputProps} value={internalValue} onChange={handleChange} />;
};

// Full-featured TextField version (includes label, helper text, etc.)
export const DebouncedTextField = ({
  delayTimeout = 500,
  onChange,
  value: externalValue = '',
  minLength,
  forceNotifyOnMount = false,
  ...textFieldProps
}) => {
  const [internalValue, setInternalValue] = useState(externalValue);
  const timeoutRef = useRef(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    setInternalValue(externalValue);
  }, [externalValue]);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (isFirstRender.current) {
      isFirstRender.current = false;
      if (!forceNotifyOnMount) {
        return;
      }
    }

    if (minLength !== undefined && internalValue.length < minLength) {
      return;
    }

    timeoutRef.current = setTimeout(() => {
      if (onChange && internalValue !== externalValue) {
        onChange({ target: { value: internalValue } });
      }
    }, delayTimeout);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [internalValue, delayTimeout, onChange, externalValue, minLength, forceNotifyOnMount]);

  const handleChange = useCallback((event) => {
    setInternalValue(event.target.value);
  }, []);

  return <TextField {...textFieldProps} value={internalValue} onChange={handleChange} />;
};

export default DebouncedInput;
